/**
 * Core Upload Hook
 * Unified file upload logic for both Platform and Team contexts
 */

import axios, { AxiosProgressEvent, CancelTokenSource } from 'axios'
import { useState } from 'react'

import { DEFAULT_UPLOAD_CONCURRENCY } from '@/consts/upload'
import { CHUNK_SIZE, FileUploaderClient, UploadPartResponse } from '@/pages/data-mgr/file-client.ts'

import * as fsOps from '../fs-operations'
import { FSContext, UploadPausedError } from '../types'

export enum UPLOAD_RESPONSE_CODE {
  SUCCESS = 0,
  FILE_ALREADY_EXISTS = 1,
  DATADRIVE_FULL = 17,
}

export enum FILE_STATUS {
  PENDING,
  PREPARING,
  UPLOADING,
  PAUSED,
  SUCCESS,
  FAILED,
  RETRYING,
}

export interface IUploadTask {
  index: number
  file: File
  status: FILE_STATUS
  loaded: number
  name: string
  size: number
  cancelSource: CancelTokenSource
  // Progress tracking
  startTime: number // Upload start timestamp
  speed: number // Average speed in bytes/second
  // Pause/resume support for large files
  client?: FileUploaderClient
  domain?: number
  target?: string
}

/**
 * Core upload hook - accepts FSContext for platform/team abstraction
 * @param context - FS Context (Platform or Team)
 * @returns [uploadList, uploadFile, successNum]
 */
export function useUploadCore(context: FSContext) {
  const [uploadList, setUploadList] = useState<IUploadTask[]>([])
  const [successNum, setSuccessNum] = useState(0)

  const createTask = (file: File, domain: number, target: string): IUploadTask => {
    const task: IUploadTask = {
      index: -1,
      file,
      status: FILE_STATUS.UPLOADING,
      loaded: 0,
      name: file.name,
      size: file.size,
      cancelSource: axios.CancelToken.source(),
      startTime: Date.now(),
      speed: 0,
      domain,
      target,
    }

    setUploadList((queue) => {
      task.index = queue.length
      return [...queue, task]
    })

    return task
  }

  const updateTask = (task: IUploadTask, state: Partial<IUploadTask>) => {
    if (task.index < 0) {
      return
    }

    // Calculate average speed when loaded is updated
    if (state.loaded !== undefined && state.loaded > 0) {
      const elapsedSeconds = (Date.now() - task.startTime) / 1000
      if (elapsedSeconds > 0) {
        state.speed = state.loaded / elapsedSeconds
      }
    }

    Object.assign(task, state)
    setUploadList((queue) => [...queue])
  }

  const uploadFile = async (file: File, target: string, domain: number) => {
    const task = createTask(file, domain, target)
    const api = context.getApiInstance()

    const onUploadProgress = (e: AxiosProgressEvent) => {
      updateTask(task, { loaded: e.loaded })
    }

    // Large file: Use parallel chunk upload
    if (file.size > CHUNK_SIZE * 4) {
      let completedChunks = 0

      const client = new FileUploaderClient({
        chunkSize: CHUNK_SIZE,
        concurrency: DEFAULT_UPLOAD_CONCURRENCY,
        requestOptions: {
          retryTimes: 3,
          initFilePartUploadFunc: async () => {
            // Save client reference to task for pause/resume
            updateTask(task, { client, domain, target })
          },
          uploadPartFileFunc: async (options): Promise<UploadPartResponse> => {
            const formData = new FormData()
            formData.append('file', options.chunk)
            formData.append('file_name', `${target}${file.name}`)
            formData.append('file_size', file.size.toString())
            formData.append('checksum', options.fileMd5)
            formData.append('block_index', options.index.toString())
            formData.append('block_size', CHUNK_SIZE.toString())
            formData.append('checksum_alg', '1')

            // Get fresh token for each chunk (uses 60s cache internally)
            const token = await context.getToken(domain)

            const response = await api.post('/vol/block_file', formData, {
              domain,
              timeout: 0,
              noErrMsg: true,
              errMsg: false,
              headers: {
                Authorization: token,
              },
              signal: options.signal,
            })

            const { code, info } = response

            if (code === UPLOAD_RESPONSE_CODE.DATADRIVE_FULL) {
              updateTask(task, {
                status: FILE_STATUS.FAILED,
              })
              client.cancel()
              throw new Error('Datadrive full')
            }

            if (
              code !== UPLOAD_RESPONSE_CODE.SUCCESS &&
              code !== UPLOAD_RESPONSE_CODE.FILE_ALREADY_EXISTS
            ) {
              throw new Error(response.msg || `Chunk upload failed with code ${code}`)
            }

            if (
              code === UPLOAD_RESPONSE_CODE.SUCCESS ||
              code === UPLOAD_RESPONSE_CODE.FILE_ALREADY_EXISTS
            ) {
              completedChunks++
              onUploadProgress({
                loaded: Math.min(completedChunks * CHUNK_SIZE, file.size),
                bytes: completedChunks * CHUNK_SIZE,
                lengthComputable: true,
              } as AxiosProgressEvent)
            }

            // Return response with blkWritten for parallel upload optimization
            return { code, info }
          },
          finishFilePartUploadFunc: async () => {
            updateTask(task, { status: FILE_STATUS.SUCCESS, loaded: file.size })
          },
        },
      })

      try {
        await client.uploadFile(file)
        fsOps.refresh(context, { domain })
        setSuccessNum((num) => num + 1)
      } catch (e) {
        if (e instanceof UploadPausedError) {
          updateTask(task, { status: FILE_STATUS.PAUSED })
        } else {
          console.error(e)
          updateTask(task, { status: FILE_STATUS.FAILED })
        }
      }
      return
    }

    // Small file: Direct upload
    const data = new FormData()
    data.append('file', file)
    const params = { path: `${target}${file.name}` }

    // Get fresh token for upload
    const token = await context.getToken(domain)

    const { code } = await api.post('/vol', data, {
      domain,
      timeout: 0,
      params,
      onUploadProgress,
      cancelToken: task.cancelSource.token,
      noErrMsg: true,
      headers: {
        Authorization: token,
      },
    })

    updateTask(task, { status: code === 0 ? FILE_STATUS.SUCCESS : FILE_STATUS.FAILED })
    if (code === 0) {
      fsOps.refresh(context, { domain })
      setSuccessNum((num) => num + 1)
    }
  }

  const pauseTask = (task: IUploadTask) => {
    if (task.client && task.status === FILE_STATUS.UPLOADING) {
      task.client.pause()
      // Status will be updated when UploadPausedError is caught
    }
  }

  const resumeTask = async (task: IUploadTask) => {
    if (!task.client || task.status !== FILE_STATUS.PAUSED) {
      return
    }

    updateTask(task, { status: FILE_STATUS.UPLOADING })
    task.client.resume()

    try {
      await task.client.resumeUpload()
      updateTask(task, { status: FILE_STATUS.SUCCESS, loaded: task.size })
      if (task.domain !== undefined) {
        fsOps.refresh(context, { domain: task.domain })
      }
      setSuccessNum((num) => num + 1)
    } catch (e) {
      if (e instanceof UploadPausedError) {
        updateTask(task, { status: FILE_STATUS.PAUSED })
      } else {
        console.error(e)
        updateTask(task, { status: FILE_STATUS.FAILED })
      }
    }
  }

  return { uploadList, uploadFile, successNum, pauseTask, resumeTask }
}
